import { Alert, Box, Button, Link, Paper, TextField, Typography } from '@mui/material';
import { requestMagicLink } from '@servicedesk/ui';
import { type FormEvent, useState } from 'react';
import { authRedirectUri, config } from '../config';

/** Passwordless sign-in screen for the staff self-service portal. */
export function MagicSignIn() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [devLink, setDevLink] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setDevLink(null);
    try {
      const res = await requestMagicLink(config.apiBaseUrl, email.trim(), authRedirectUri);
      setDevLink(res.devLink ?? null);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          IT Help
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your work email and we'll send you a secure sign-in link.
        </Typography>

        {status === 'sent' && !devLink && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Check your inbox — if that address is recognised, a sign-in link is on its way.
          </Alert>
        )}
        {status === 'sent' && devLink && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Testing mode — use your sign-in link: <Link href={devLink}>Sign in</Link>
          </Alert>
        )}
        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Something went wrong. Please try again.
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <TextField
            label="Work email"
            type="email"
            fullWidth
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained" fullWidth disabled={status === 'sending'}>
            {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
